import { motion } from "framer-motion";
import { PenTool, Rocket, LifeBuoy } from "lucide-react";
import { useLang } from "@/lib/i18n";

const ICONS = [PenTool, Rocket, LifeBuoy];

const storefront =
  "https://images.unsplash.com/photo-1575663620136-5ebbfcc2c597?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxzbWFsbCUyMGxvY2FsJTIwYnVzaW5lc3MlMjBvd25lciUyMHN0b3JlZnJvbnQlMjBwcm91ZHxlbnwwfHx8fDE3ODQyMjg2OTZ8MA&ixlib=rb-4.1.0&q=85";

export default function WhatWeDo() {
  const { t } = useLang();
  return (
    <section id="what-we-do" data-testid="what-we-do-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <p className="font-mono-x text-xs uppercase tracking-[0.25em] text-primary mb-5">{t.whatwedo.eyebrow}</p>
            <h2 className="font-heading font-bold tracking-tight leading-none text-4xl sm:text-5xl">
              {t.whatwedo.heading}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-5 text-base lg:text-lg text-muted-foreground leading-relaxed"
          >
            {t.whatwedo.paragraph}
          </motion.p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {t.whatwedo.services.map((s, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6 }}
                data-testid={`service-card-${i}`}
                className="group relative rounded-2xl border border-border bg-card p-8 hover:border-primary/50 transition-colors duration-300 overflow-hidden"
              >
                <span className="absolute right-5 top-4 font-heading text-5xl font-black text-foreground/5 group-hover:text-primary/10 transition-colors">
                  {`0${i + 1}`}
                </span>
                <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="font-heading text-2xl font-bold mb-3">{s.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="mt-6 relative rounded-2xl overflow-hidden border border-border h-64 md:h-80"
        >
          <img src={storefront} alt="Local business open for customers" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/50 to-transparent" />
          <div className="absolute inset-0 flex items-center px-8 md:px-14">
            <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold max-w-lg leading-tight">
              {t.whatwedo.bannerA} <span className="text-primary">{t.whatwedo.bannerB}</span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
